<template>
  <div class="pdpmodelx">
    <!-- slider used for full screen -->
    <ProductCarousel v-if="props.product.images.nodes.length > 1"
      class="fullscreenslider fixed top-0 bg-white z-50 max-h-screen" :class="{ 'showfull': isExpanded }"
      :images="props.product.images.nodes.slice(0, -1)" @mytoggle="toggleSlideshow" @closeslideshow="closeSlideshow"
      :isExpanded="isExpanded" />
    <transition name="fade-in">
      <ProductRotorPopup v-if="showRotorPopup" @close="showRotorPopup = false" />
    </transition>


    <AnimationProductIn>
      <div class="pdpmodel" :class="{ 'expandedslide': isExpanded, 'closing': isClosing, 'opening': isOpening }">

        <!-- Add to cart pop up with fullscreen backdrop -->
        <transition name="fade-in">
          <ProductPopupBackdrop v-if="showPopup" />
        </transition>
        <transition name="fade-down">
          <CartPopup v-if="showPopup && popupCartline" :cartline="popupCartline" @close="showPopup = false" />
        </transition>


        <!-- page content -->

        <UiContentwrapper class="mt-28 md:mt-28 pt-0 md:pt-8 mb-20 gap-4">
          <!-- :style="'background-image: url(/facit/pdp.png);background-size:100% auto'"-->

          <!-- L E F T C O L U M N -->
          <div class="col-span-10 md:col-span-6">


            <ProductDetails :product="props.product" :isCustom="isCustomWheelset" class="block md:hidden" />

            <!-- Slider used for not full screen -->
            <ProductCarousel v-if="props.product.images.nodes.length > 1" class="expandwrapper bg-gravaalightwarmgray"
              :images="props.product.images.nodes.slice(0, -1)" @mytoggle="toggleSlideshow" :isExpanded="isExpanded" />

            <!-- "Lifestyle/action image (last)"-->
            <UiArticleImage class="col-span-6 mt-8"
              :article="{ title: product.title, image: props.product.images.nodes[props.product.images.nodes.length - 1].url }"
              :title="props.product.title" :modifiers="{
                auto: 'format,compress',
                fit: 'crop',
                'fp-x': 0.5,
                'fp-y': 0.5,
                ar: '679:398'
              }" />

          </div>


          <!-- Product Details Right Column (Hidden when Expanded) -->
          <div class="col-span-10 md:col-span-4 rightcol bg-white" data-style="transform: translateX(20%)">

            <!--button @click="togglePopup">Show Popup</button-->

            <!-- D E S K T O P  – D E T A I L S -->

            <ProductDetails :product="props.product" :isCustom="isCustomWheelset" class="hidden md:block" />

            <!-- Description -->
            <ProductDescription :product="props.product" />


            <!-- Variant Selection -->
            <div v-if="hasOptions" class="mt-7 border-t pb-2 mb-4"
              :class="{ 'border-b border-black-50': !isCustomWheelset }">
              <ProductCustomHubtype v-if="props.product.customHubType"
                :customHubType="props.product.customHubType.value" />

              <div v-for="option in props.product.options" :key="option.name" class="mt-7">
                <p class="block small mt-4 mb-1">{{ option.name }}</p>
                <UiCustomDropdown :option="option" class="w-full" :selectedOption="getSelectedOption(option)"
                  @change="(value) => setOption(value, option)" />
              </div>

            </div>

            <!-- Custom Wheelset Configuration Fields -->
            <ProductWheelsetCustomFields v-if="isCustomWheelset" :product="props.product" v-model="customWheelsetConfig"
              @updateUrl="updateCustomFieldUrl" />

            <!-- Help link for custom wheelsets (after all options) -->
            <div v-if="isCustomWheelset && hasOptions" class="mt-5 mb-4">
              <p @click="toggleRotorPopup"
                class="small cursor-pointer text-gravaadarkbrown underline underline-offset-2">
                Need help to choose freehub or rotors?
              </p>
            </div>

            <!-- Add to Cart & cart confirmation popup -->
            <div v-if="!isCustomWheelset" class="mt-4  pb-6 relative">
              <UiRippleButton class="brown w-full mt-2 addtocartbutton"
                :class="{ 'available': currentVariant?.node?.availableForSale }" @click="addToCart"
                :disabled="!currentVariant">
                ADD TO CART
              </UiRippleButton>
              <p v-if="currentVariant" class="text-red-600 absolute top-0 outofstockcombo pt-4"
                :class="{ 'unavailable': !currentVariant?.node?.availableForSale }">Combination out of stock</p>
            </div>

            <!-- Custom Wheelset Quote Button -->
            <div v-else class="mt-4 pb-6">
              <ProductWheelsetQuoteModal :product="props.product" :currentVariant="currentVariant"
                :customConfig="customWheelsetConfig" />
            </div>

            <!-- Shipping and returns -->

            <div v-if="shippingArticle">
              <UiFaqItem :article="shippingArticle" :small="true" />
            </div>
            <div v-if="warrantyArticle" class="border-b border-black">
              <UiFaqItem :article="warrantyArticle" :small="true" />
            </div>
          </div><!-- end right column -->
        </UiContentwrapper>

      </div>
    </AnimationProductIn>
  </div>
</template>

<script setup lang="ts">
import type { OptionModel } from "~/types/shopify";
import { ref, computed, onMounted, defineAsyncComponent } from "vue";

import { useCartStore } from "~/stores/cartStore";
import { useBlogStore } from "~/stores/blogStore";




const UiFaqItem = defineAsyncComponent(() => import('@/components/ui/FaqItem.vue'));

const cartStore = useCartStore();
const blogStore = useBlogStore();

const route = useRoute();
const router = useRouter();

const showPopup = ref(false);
const showRotorPopup = ref(false);
const popupCartline = ref({});

const isExpanded = ref(false);
const isClosing = ref(false);
const isOpening = ref(false);

const mounted = ref(false);
const shippingArticle = ref(null);
const warrantyArticle = ref(null);


interface Blog {
  title: string;
  [key: string]: any;
}

const warrantyBlog = ref<Blog>({ title: '' });
const faqBlog = ref<Blog>({ title: '' });


function togglePopup() {
  // used only for testing
  showPopup.value = !showPopup.value;
}

function toggleRotorPopup() {

  showRotorPopup.value = !showRotorPopup.value;
}

function toggleSlideshow() {
  if (isClosing.value) return;
  if (isOpening.value) return;

  if (isExpanded.value) {
    isClosing.value = true;
    setTimeout(() => {
      isExpanded.value = false;
      isClosing.value = false;
    }, 400);
  } else {
    isOpening.value = true;
    setTimeout(() => {
      isExpanded.value = true;
      isOpening.value = false;
    }, 400);
  }

}

function closeSlideshow() {
  isClosing.value = true;
  setTimeout(() => {
    isExpanded.value = false;
    isClosing.value = false;
  }, 200);
}

const props = defineProps({
  product: {
    type: Object,
    required: true,
  },
});


onMounted(async () => {
  mounted.value = true;
  popupCartline.value = {};
  showPopup.value = false;
  showRotorPopup.value = false;

  // HANTERA VARIANT-ID I URL
  const variantId = route.query.variant;
  if (variantId) {
    const fullGid = `gid://shopify/ProductVariant/${variantId}`;
    const match = props.product.variants.edges.find(v => v.node.id === fullGid);
    if (match) {
      const updatedQuery = { ...route.query };

      match.node.selectedOptions.forEach(option => {
        updatedQuery[option.name] = option.value;
      });

      // Ta bort ?variant från URL efter vi tolkat den
      delete updatedQuery.variant;

      router.replace({
        path: route.path,
        query: updatedQuery,
      });
    }
  }

  // Initialize custom wheelset config from URL query parameters
  if (isCustomWheelset.value) {
    customWheelsetConfig.value = {
      riderWeight: (route.query.riderWeight as string) || '',
      rimType: (route.query.rimType as string) || '',
      rimHeight: (route.query.rimHeight as string) || '',
      tireWidth: (route.query.tireWidth as string) || '',
      rimBrand: (route.query.rimBrand as string) || '',
      otherRequests: (route.query.otherRequests as string) || ''
    };
  }

  // Ladda relaterade artiklar
  blogStore.fetchBlogByHandle('pdpblog').then(async (res) => {
    if (res) {
      warrantyBlog.value = res;
      warrantyArticle.value = await blogStore.getArticleByHandle('2-year-warranty') || null;
    }
  });

  blogStore.fetchBlogByHandle('pdpblog').then(async (res) => {
    if (res) {
      faqBlog.value = res;
      shippingArticle.value = await blogStore.getArticleByHandle('shipping-return-policy') || null;
    }
  });
});



const hasOptions = computed(() => {
  return props.product.options[0].name !== 'Title';
});

const isCustomWheelset = computed(() => {
  return props.product.tags?.includes('custom-wheelset') || false;
});

// Custom wheelset configuration state
const customWheelsetConfig = ref({
  riderWeight: '',
  rimType: '',
  rimHeight: '',
  tireWidth: '',
  rimBrand: '',
  otherRequests: ''
});


const currentVariant = computed(() => {
  if (props.product) {
    if (props.product.variants.edges.length === 1) {
      return props.product.variants.edges[0];
    }
    return props.product.variants.edges.find((variant: { node: { selectedOptions: { value: string }[] } }) =>
      variant.node.selectedOptions.every(
        (option, index) => option.value === route.query[props.product.options[index].name]
      )
    );
  }
});


async function addToCart() {

  if (currentVariant.value) {
    console.log(currentVariant.value.node.id);
    const response = await cartStore.addToCart(currentVariant.value.node.id);
    if (response.success) {
      console.log('Product added to cart:', response.cartLine);
      popupCartline.value = response.cartLine;
      showPopup.value = true;
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });



    } else {
      console.error('Failed to add product to cart:', response.error);
      // Optionally, show an error message to the user
    }
  } else {
    console.warn('No variant selected');
  }
}

function setOption(value: string, option: OptionModel) {
  //console.log('setOption');
  //console.log(value);
  //console.log(option);
  router.replace({
    path: route.path,
    query: {
      ...route.query,
      [option.name]: value,
    },
  });
}

function getSelectedOption(option: OptionModel): string {
  const params = new URLSearchParams(Object.entries(route.query).map(([key, value]) => [key, value as string]))
  return params.get(option.name) || ''
}

function updateCustomFieldUrl(field: string, value: string) {
  router.replace({
    path: route.path,
    query: {
      ...route.query,
      [field]: value,
    },
  });
}



</script>

<style scoped>
.addtocartbutton,
.outofstockcombo {
  opacity: 0;
  transition: opacity 0.3s;
  pointer-events: none;
}

.addtocartbutton.available,
.outofstockcombo.unavailable {
  opacity: 1;
  pointer-events: all;
}


.opening .rightcol {
  animation: moveOutAnimation .4s ease forwards;
}

.rightcol {
  transition: transform 0.3s;
  transform: translateX(0%);
}

.closing .rightcol {
  transform: translateX(159%);
}

.expandedslide .rightcol {}

.expandwrapper {
  transition: opacity 0.3s;
  transition-delay: .1s;
  opacity: 1;


}

.expandedslide .expandwrapper {
  opacity: 0;

}

.opening .expandwrapper {
  opacity: 0;

}

@keyframes moveOutAnimation {
  0% {
    transform: translateX(0%);
    opacity: 1;
  }

  100% {
    transform: translateX(159%);
    opacity: 0;
  }

}


.fullscreenslider,
.fullscreenslider.closing {
  transform: translateY(-100%);
  pointer-events: none;
  opacity: 0;
  transform-origin: 50% 50%;

  transition: all 0.3s;
}

.fullscreenslider.showfull {
  transform: translateY(0%);
  pointer-events: all;
  opacity: 1;
  transition-delay: .3s;
}
</style>
